import { Flex } from "antd";
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    AssignmentResponseType,
    Market,
    TaskAssignment,
    TaskStatus,
} from "tm-shared/types";
import { isTaskAssignment } from "tm-shared/utils";

import { AssignmentTaskInfo } from "@/components/AssignmentTaskInfo";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import { SVScanButton } from "@/components/SVScanButton";
import { ASSIGNMENT_VOCABULARY, ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useMarketDistance } from "@/hooks/useMarketDistance";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { TaskFinishButton } from "@/pages/WorkPlan/Tasks/components/TaskFinishButton";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import {
    clearTaskPayload,
    mutateAssignmentsEntityPhotos,
    patchAssignmentsEntity,
} from "@/store/slices/progress";
import { setTaskCompleted } from "@/store/slices/work";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { Bool } from "./components/Bool";
import { Photo } from "./components/Photo";
import { PhotoText } from "./components/PhotoText";
import { Text } from "./components/Text";
import { StyledBlock, StyledContainer } from "./Styled";

interface AssignmentsTaskParams {
    task: TaskAssignment;
    market: Market;
}

export const AssignmentTaskPage: FC<AssignmentsTaskParams> = ({
    task,
    market,
}) => {
    const marketId = market.id;
    const taskId = task.id;
    const assignmentType = task.context.additionalData.assignmentType;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { firstName, lastName } = useAppSelector((s) => s.user);

    const currentPayload = useAppSelector((s) => s.progress.customTask[taskId]);

    const { distance } = useMarketDistance({ marketGeo: market.geo });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                marketName: market.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, market.name, task.name],
    );

    const isFinishButtonVisible = useMemo(() => {
        if (task.context.responseType === AssignmentResponseType.Bool) {
            return currentPayload?.status !== TaskStatus.Incomplete;
        }

        return Boolean(
            currentPayload?.comment || currentPayload?.photos?.length,
        );
    }, [
        currentPayload?.comment,
        currentPayload?.photos?.length,
        currentPayload?.status,
        task.context.responseType,
    ]);

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const photoIds = currentPayload?.photos ?? [];
        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: currentPayload },
            marketId,
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,
        ];

        if (photoIds.length > 0) {
            commands.push(
                makeCommand({
                    type: "updatePhotoStorage",
                    title: "Синхронізація сховища",
                    params: photoIds,
                    options: {
                        stopOnError: false,
                    },
                }),
            );
        }

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: {
                    marketId,
                    taskId,
                },
                commands,
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ marketId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });
    });

    const onChangeComment = useCallback(
        (evt: ChangeEvent<HTMLTextAreaElement>) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { comment: evt.target.value },
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const onChangeStatus = useCallback(
        (newStatus: TaskStatus) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { status: newStatus },
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            dispatch(
                mutateAssignmentsEntityPhotos({
                    taskId,
                    option: "add",
                    photo,
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            dispatch(
                mutateAssignmentsEntityPhotos({
                    taskId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const renderContent = useMemo(() => {
        if (!task || !isTaskAssignment(task)) return null;

        const commonProps = {
            marketId,
            taskId,
            payload: currentPayload,
            captions,
            assignmentType,
        };

        switch (task.context.responseType) {
            case AssignmentResponseType.Bool:
                return (
                    <Bool {...commonProps} onChangeStatus={onChangeStatus} />
                );

            case AssignmentResponseType.Text:
                return (
                    <Text
                        {...commonProps}
                        title={task.name}
                        onChangeComment={onChangeComment}
                    />
                );

            case AssignmentResponseType.Photo:
                return (
                    <Photo
                        {...commonProps}
                        title={task.name}
                        onUploadPhoto={onUploadPhoto}
                        onDeletePhoto={onDeletePhoto}
                    />
                );

            case AssignmentResponseType.PhotoText:
                return (
                    <PhotoText
                        {...commonProps}
                        title={task.name}
                        onChangeComment={onChangeComment}
                        onUploadPhoto={onUploadPhoto}
                        onDeletePhoto={onDeletePhoto}
                    />
                );

            default:
                return <div>Невідомий тип доручення</div>;
        }
    }, [
        task,
        marketId,
        taskId,
        currentPayload,
        captions,
        onChangeStatus,
        onChangeComment,
        onUploadPhoto,
        onDeletePhoto,
    ]);

    const assignmentLabel = ASSIGNMENT_VOCABULARY[assignmentType]?.label;

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <SVScanButton marketId={marketId} taskId={taskId} />
                }
            />

            <MainContainer>
                <StyledContainer>
                    <Flex vertical gap="16px">
                        <StyledBlock>
                            <AssignmentTaskInfo
                                assignmentType={assignmentLabel}
                                description={task.context.description}
                                images={task.context.files}
                            />
                        </StyledBlock>

                        {renderContent}
                    </Flex>

                    <TaskFinishButton
                        onClick={handleComplete}
                        visible={isFinishButtonVisible}
                        loading={completeLoading}
                        padding="0px"
                    />
                </StyledContainer>
            </MainContainer>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};

export default AssignmentTaskPage;
